import { useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";

const SHOW_AFTER_PX = 300;
const SCROLL_DURATION_MS = 650;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export default function ScrollToTopButton() {
  const [isVisible, setIsVisible] = useState(false);
  const frameRef = useRef(null);
  const tickingRef = useRef(false);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      reducedMotionRef.current = mediaQuery.matches;
    };

    updateMotionPreference();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateMotionPreference);
    } else {
      mediaQuery.addListener(updateMotionPreference);
    }

    const updateVisibility = () => {
      setIsVisible(window.scrollY > SHOW_AFTER_PX);
      tickingRef.current = false;
    };

    const onScroll = () => {
      if (tickingRef.current) {
        return;
      }
      tickingRef.current = true;
      window.requestAnimationFrame(updateVisibility);
    };

    updateVisibility();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateMotionPreference);
      } else {
        mediaQuery.removeListener(updateMotionPreference);
      }
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const scrollToTop = () => {
    if (typeof window === "undefined") {
      return;
    }

    if (frameRef.current) {
      window.cancelAnimationFrame(frameRef.current);
    }

    if (reducedMotionRef.current) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const startY = window.scrollY;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / SCROLL_DURATION_MS, 1);
      const nextY = Math.round(startY * (1 - easeOutCubic(progress)));

      window.scrollTo(0, nextY);

      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(tick);
      } else {
        frameRef.current = null;
      }
    };

    frameRef.current = window.requestAnimationFrame(tick);
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Scroll back to the top of the page"
      title="Back to top"
      className={`scroll-top-button fixed z-30 inline-flex items-center justify-center rounded-full border bg-white/92 text-[var(--gold)] shadow-[0_18px_40px_-20px_rgba(26,26,26,0.45)] backdrop-blur-md transition-all duration-200 ease-out hover:-translate-y-1 hover:bg-[var(--gold)] hover:text-[var(--button-text)] focus-visible:outline-none focus-visible:ring-4 ${
        isVisible
          ? "pointer-events-auto opacity-100 translate-y-0"
          : "pointer-events-none opacity-0 translate-y-3"
      } h-11 w-11 sm:h-12 sm:w-12`}
      style={{ borderColor: "var(--border-gold)", boxShadow: "0 18px 40px -20px rgba(26,26,26,0.45)", }}
    >
      <ChevronUp className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
      <span className="sr-only">Scroll to top</span>
    </button>
  );
}
